import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { CAREERS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { role: "", name: "", phone: "", email: "", cdl_class: "", experience_years: "", notes: "" };

export function CareersForm() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.role || !form.name || !form.email || !form.phone) {
      toast.error("Please fill in role, name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/careers-applications`, form);
      toast.success("Application received. We'll be in touch within 48 hours.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Submission failed. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-[hsl(var(--lh-bone))]/15 bg-[hsl(var(--lh-ink))] p-8 md:p-10" data-testid="careers-form">
      <div className="flex items-baseline justify-between mb-8">
        <h3 className="font-display text-3xl">Apply in two minutes.</h3>
        <span className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-[hsl(var(--lh-bone))]/50">Form · 01</span>
      </div>

      <div className="space-y-6">
        <div>
          <label className="lh-label">Role you&apos;re after</label>
          <select className="lh-select" value={form.role} onChange={update("role")} data-testid="careers-role">
            <option value="">Select a role…</option>
            {CAREERS.roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="lh-label">Full name</label>
            <input className="lh-input" value={form.name} onChange={update("name")} data-testid="careers-name"/>
          </div>
          <div>
            <label className="lh-label">Phone</label>
            <input className="lh-input" value={form.phone} onChange={update("phone")} data-testid="careers-phone"/>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <label className="lh-label">Email</label>
            <input type="email" className="lh-input" value={form.email} onChange={update("email")} data-testid="careers-email"/>
          </div>
          <div>
            <label className="lh-label">CDL Class</label>
            <select className="lh-select" value={form.cdl_class} onChange={update("cdl_class")} data-testid="careers-cdl">
              <option value="">—</option>
              <option value="A">Class A</option>
              <option value="B">Class B</option>
              <option value="C">Class C</option>
              <option value="None">None / N/A</option>
            </select>
          </div>
        </div>
        <div>
          <label className="lh-label">Years experience</label>
          <input className="lh-input" placeholder="e.g. 6" value={form.experience_years} onChange={update("experience_years")} data-testid="careers-experience"/>
        </div>
        <div>
          <label className="lh-label">Anything we should know?</label>
          <textarea rows={4} className="lh-input resize-none" value={form.notes} onChange={update("notes")} data-testid="careers-notes"/>
        </div>
        <button type="submit" disabled={submitting} className="lh-btn-primary w-full justify-center" data-testid="careers-submit">
          {submitting ? "Sending…" : "Send Application"} <ArrowRight size={14} strokeWidth={2.5}/>
        </button>
      </div>
    </form>
  );
}
